import './MatchListByGroupsMobile.scss';

import { ArrowLeft, ArrowRight } from '@mui/icons-material';
import { Skeleton } from '@mui/lab';
import { IconButton, Pagination } from '@mui/material';
import { EventStatusEnum } from 'domain/event/EventStatusEnum';
import { MatchGroup } from 'domain/event/match/MatchGroup';
import {
  toMatchLigueModel,
  UnpyMatch,
  UnpyMatchFilters,
} from 'domain/event/match/UnpyMatch';
import { SystemPointsEnum } from 'domain/event/SystemPoints.enum';
import { ListElements } from 'primary/Components/ListElements/ListElements';
import { SkeletonNoContent } from 'primary/Components/SkeletonNoContent/SkeletonNoContent';
import { TeamScoreEditRowMobile } from 'primary/events/components/matchs/edit/mobile/TeamScoreEditRowMobile';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useFetchWithFilterPage } from 'primary/hooks/useFetchWithFilterPage';
import { useTranslate } from 'primary/hooks/useTranslate';
import React, { useEffect } from 'react';

type MatchListViewByGroupProps = {
  eventId?: number;
  filters?: UnpyMatchFilters;
  asView?: boolean;
};
export const MatchListByGroupsMobile = ({
  eventId,
  filters,
  asView,
}: MatchListViewByGroupProps) => {
  const t = useTranslate();
  const { matchsRepository } = useContextDependency();
  const [group, setGroup] = React.useState<MatchGroup | undefined>();
  const {
    setFilters,
    activeFilters,
    fetchedResource,
    nextPage,
    loading,
    goToPage,
    retry,
    addToCurrentPage,
  } = useFetchWithFilterPage<MatchGroup, UnpyMatchFilters>({
    keyStoreFilters: 'groups-ligue-filters-mobile',
    staticFilters: {
      idEvent: eventId,
    },
    filters: filters ?? {},
    callApi: (filters, page) => matchsRepository.searchGroup(filters, page, -1),
    conditionFetch: (activeFilters) => !!activeFilters?.idEvent,
  });

  useEffect(() => {
    if (fetchedResource?.content?.[0] && !group) {
      setGroup(fetchedResource.content[0]);
    }
  }, [fetchedResource]);

  useEffect(() => {
    setFilters({ ...(activeFilters?.filters ?? {}), ...filters });
  }, [filters]);

  const currentIndex = fetchedResource?.content?.findIndex((g) => g.id === group?.id);
  return (
    <div className={'matchListByGroupsMobile'}>
      <div className={'-groupsSelector'}>
        <SkeletonNoContent displayContent={!loading}>
          <IconButton
            onClick={(e) => {
              if (!currentIndex) return;
              const newGroup = fetchedResource?.content?.[currentIndex - 1];
              setGroup(newGroup);
            }}
            disabled={!currentIndex || loading}
            className={'-groupsSelector__arrow -left'}
          >
            <ArrowLeft />
          </IconButton>
          {!loading && !fetchedResource?.content && (
            <p>{t('event.groupsSelector.noGroup')}</p>
          )}
          {!loading && group && (
            <div className={'-groupSelected'}>
              <span className={'-groupName'}>{group.title}</span>
              <span className={'-groupMatchsNumber'}>
                {t('event.matchs.numberMatchs', {
                  count: group?.totalMatchsInGroup ?? 0,
                })}
              </span>
            </div>
          )}
          <IconButton
            onClick={(e) => {
              if (currentIndex === (fetchedResource?.content?.length || 1) - 1) return;
              const newGroup = fetchedResource?.content?.[(currentIndex ?? 0) + 1];
              setGroup(newGroup);
            }}
            disabled={
              currentIndex === (fetchedResource?.content?.length || 1) - 1 || loading
            }
            className={'-groupsSelector__arrow -right'}
          >
            <ArrowRight />
          </IconButton>
        </SkeletonNoContent>
      </div>
      <div className={'matchListByGroupsMobile__matchList'}>
        {!group && <Skeleton animation="wave" height={'100%'} width="100%" />}
        {group && <MatchListOfGroup filters={filters} group={group} asView={asView} />}
      </div>
    </div>
  );
};

type MatchListByGroupProps = {
  group: MatchGroup;
  filters?: UnpyMatchFilters;
  asView?: boolean;
};
const MatchListOfGroup = ({ group, filters, asView }: MatchListByGroupProps) => {
  const { matchsRepository } = useContextDependency();
  const {
    setFilters,
    activeFilters,
    fetchedResource,
    nextPage,
    loading,
    retry,
    goToPage,
    addToCurrentPage,
  } = useFetchWithFilterPage<UnpyMatch, UnpyMatchFilters>({
    keyStoreFilters: 'matchs-ligue-filters-mobile',
    filters: {
      ...(filters ?? {}),
      idGroup: group.id,
    },
    callApi: (filters, page) => matchsRepository.search(filters, page, 10),
    conditionFetch: (activeFilters) => !!activeFilters?.idGroup,
  });
  const t = useTranslate();
  const matchsFormatted = fetchedResource?.content?.map(toMatchLigueModel);

  useEffect(() => {
    setFilters({
      ...(activeFilters?.filters ?? {}),
      ...(filters ?? {}),
      idGroup: group.id,
    });
  }, [group, filters]);

  return (
    <div className={'matchListOfGroup'}>
      <Pagination
        count={fetchedResource?.pagesCount || 1}
        page={fetchedResource?.currentPage ? fetchedResource?.currentPage + 1 : 1}
        size={'medium'}
        className={'-pagination'}
        onChange={(e, value) => goToPage(value - 1)}
      />
      {loading && <LignesSkeleton />}
      <ListElements
        loading={loading}
        empty={!loading && fetchedResource?.content.length === 0}
      >
        {!matchsFormatted?.length && <p>{t('event.matchs.noMatchsInGroup')}</p>}
        {matchsFormatted &&
          matchsFormatted.map((match) => (
            <TeamScoreEditRowMobile
              isClassementReadOnly={group.event.status !== EventStatusEnum.ON_GOING}
              asView={asView}
              key={match.id}
              className={'-item'}
              match={match.unpyMatch}
              systemPoint={group.event.pointSystem ?? SystemPointsEnum.DEFAULT}
              onEdited={retry}
              typeEvent={group.event.typeEvent.code}
            />
          ))}
      </ListElements>
      <Pagination
        count={fetchedResource?.pagesCount || 1}
        page={fetchedResource?.currentPage ? fetchedResource?.currentPage + 1 : 1}
        size={'medium'}
        className={'-pagination'}
        onChange={(e, value) => goToPage(value - 1)}
      />
    </div>
  );
};

const LignesSkeleton = () => {
  return (
    <>
      {Array(5)
        .fill(0)
        .map((val, index) => (
          <div style={{ width: '100%', height: '60px' }} key={index}>
            <Skeleton animation="wave" height={'100%'} width="100%" />
          </div>
        ))}
    </>
  );
};
